import React from 'react';
import { Link } from '@tanstack/react-router';
import { useListTasks } from '../../../../api/tasks';
import { ClipboardList, Plus } from 'lucide-react';

import { useTranslation } from 'react-i18next';

const LIST_ITEM_CLASS =
  'group flex w-full items-center gap-3 rounded-lg pl-0 pr-3 py-2.5 text-left text-sm transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-sm';
const ICON_WRAPPER_CLASS =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-orange-100 group-hover:text-orange-700';

export const TasksHoverContent: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const {
    data: tasks,
    isLoading,
    error,
  } = useListTasks({}, { staleTime: 60_000 });

  if (isLoading) {
    return (
      <div className="flex w-max max-w-[360px] min-w-[260px] flex-col items-center justify-center gap-2 px-4 py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-orange-500 dark:border-gray-700" />
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {t('loadingTasks')}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-max max-w-[360px] min-w-[260px] px-4 py-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('couldNotLoadTasks')}
        </p>
        <Link
          to="/tasks"
          className="mt-3 inline-flex items-center text-sm font-medium text-orange-600 hover:text-orange-700"
        >
          {t('goToTasks')}
        </Link>
      </div>
    );
  }

  const taskList = tasks ?? [];
  const topThree = taskList.slice(0, 3);

  return (
    <div className="w-max max-w-[360px] min-w-[260px]">
      <div className="py-0.5">
        {topThree.length === 0 ? (
          <p className="py-2 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('noTasksYet')}
          </p>
        ) : (
          topThree.map((task) => (
            <Link
              key={task.id}
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              className={LIST_ITEM_CLASS}
              data-testid={`task-hover-item-${task.id}`}
            >
              <span className={ICON_WRAPPER_CLASS}>
                <ClipboardList className="h-4 w-4 stroke-[1.8]" />
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
                {task.name}
              </span>
            </Link>
          ))
        )}
      </div>
      <div className="mt-1.5 border-t border-gray-100 pt-1.5 dark:border-gray-800">
        <Link
          to="/tasks"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-orange-600 py-2.5 text-xs font-medium text-white transition-colors hover:bg-orange-700"
          data-testid="tasks-create-task"
        >
          <Plus className="h-4 w-4" />
          {t('createTask')}
        </Link>
      </div>
    </div>
  );
};
